import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import Layout from './components/Layout/Layout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import DiscoveryPage from './pages/DiscoveryPage';
import AuthorityAgentPage from './pages/AuthorityAgentPage';
import ClientsPage from './pages/ClientsPage';
import SitesPage from './pages/SitesPage';
import InvoicesPage from './pages/InvoicesPage';
import EmailsPage from './pages/EmailsPage';
import LeadsPage from './pages/LeadsPage';
import ContactsPage from './pages/ContactsPage';
import AnalyticsPage from './pages/AnalyticsPage';
import PipelineManagementPage from './pages/PipelineManagementPage';
import LoadingSpinner from './components/Common/LoadingSpinner';
import CustomersPage from './pages/CustomersPage';
import CustomerDetailPage from './pages/CustomerDetailPage';
import REV108Page from './pages/REV108Page';
import AttentionEnginePage from './pages/AttentionEnginePage';
import StratagemNexus from './pages/StratagemNexus';
import OpportunityDetailPage from './pages/OpportunityDetailPage';
import OpportunityCommandCenter from './pages/OpportunityCommandCenter';
import CommunicationsPortal from './pages/CommunicationsPortal';
import IronDomePage from './pages/IronDomePage';
import OpsDashboardPage from './pages/OpsDashboardPage';
import TomiDashboardPage from './pages/TomiDashboardPage';
import CoinbaseDerivativesPage from './pages/CoinbaseDerivativesPage';
import KazeDashboardPage from './pages/KazeDashboardPage';
import TomiCommandCenterPage from './pages/TomiCommandCenterPage';
import TomiV2CommandPage from './pages/TomiV2CommandPage';
import TopStepCommandCenterPage from './pages/TopStepCommandCenterPage';
import TopStepAccount2Page from './pages/TopStepAccount2Page';
import TopStepAccount3Page from './pages/TopStepAccount3Page';
import TopStep100KChallengePage from './pages/TopStep100KChallengePage';
import TopStep150KChallengePage from './pages/TopStep150KChallengePage';
import TopStepJoshChallengePage from './pages/TopStepJoshChallengePage';
import TopStep50KChallengePage from './pages/TopStep50KChallengePage';
import TopStepExpressFundedPage from './pages/TopStepExpressFundedPage';
import TopStepExpressFunded2Page from './pages/TopStepExpressFunded2Page';
import TopStepNerdnodeFundedPage from './pages/TopStepNerdnodeFundedPage';
import TopStepBotDashboardPage from './pages/TopStepBotDashboardPage';

// Commerce pages (public)
import PricingPage from './pages/PricingPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import CheckoutSuccessPage from './pages/CheckoutSuccessPage';
import CheckoutCancelPage from './pages/CheckoutCancelPage';
import CheckoutBridgePage from './pages/CheckoutBridgePage';

// Customer-facing pages
import ServicePricingPage from './pages/ServicePricingPage';
import RetainerCheckoutPage from './pages/RetainerCheckoutPage';
import RegisterPage from './pages/RegisterPage';
import TopStepBotSignupPage from './pages/TopStepBotSignupPage';
import AccountPage from './pages/AccountPage';
import PendingPaymentPage from './pages/PendingPaymentPage';
import CustomerGate from './components/CustomerGate';

// Partner pages (public)
import { PrimesLandingPage, WhyPrimesPage } from './pages/partners';

// Payment pages (public)
import PaymentsPage from './pages/PaymentsPage';
import PaymentReceiptPage from './pages/PaymentReceiptPage';
import PaymentsAdminPage from './pages/PaymentsAdminPage';

function App() {
  const { isAuthenticated, isLoading, user } = useAuth();
  const isCustomer = user?.role === 'viewer';

  // Public routes that don't require authentication
  const PublicRoutes = (
    <Routes>
      {/* Service pricing and registration - main public pages */}
      <Route path="/pricing" element={<ServicePricingPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/topstep-bot" element={<TopStepBotSignupPage />} />
      
      {/* Partner pages - Federal Prime Contractors */}
      <Route path="/partners/primes" element={<PrimesLandingPage />} />
      <Route path="/partners/why-redex" element={<WhyPrimesPage />} />
      
      {/* Payment pages */}
      <Route path="/payments" element={<PaymentsPage />} />
      <Route path="/payments/receipt/:invoiceId" element={<PaymentReceiptPage />} />
      <Route path="/payments/admin" element={<PaymentsAdminPage />} />
      
      {/* Retainer checkout — service tier engagement flow */}
      <Route path="/retainer-checkout" element={<RetainerCheckoutPage />} />

      {/* Legacy commerce routes */}
      <Route path="/products" element={<PricingPage />} />
      <Route path="/cart" element={<CartPage />} />
      <Route path="/checkout" element={<CheckoutPage />} />
      <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
      <Route path="/checkout/cancel" element={<CheckoutCancelPage />} />
      <Route path="/checkout/bridge" element={<CheckoutBridgePage />} />
      
      {/* Auth */}
      <Route path="/login" element={<LoginPage />} />
      
      {/* Default redirect */}
      <Route path="*" element={<Navigate to="/pricing" replace />} />
    </Routes>
  );

  // Show loading spinner while checking authentication
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // If not authenticated, show public routes
  if (!isAuthenticated) {
    return PublicRoutes;
  }

  // If authenticated, show main application with all routes
  return (
    <Routes>
      {/* Login redirect for authenticated users */}
      <Route path="/login" element={<Navigate to="/dashboard" replace />} />

      {/* Payment pending gate — shown to viewer-role customers awaiting retainer */}
      <Route path="/pending-payment" element={<PendingPaymentPage />} />
      
      {/* Public pages - also available when authenticated */}
      <Route path="/pricing" element={<ServicePricingPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/topstep-bot" element={<TopStepBotSignupPage />} />
      
      {/* Partner pages - also available when authenticated */}
      <Route path="/partners/primes" element={<PrimesLandingPage />} />
      <Route path="/partners/why-redex" element={<WhyPrimesPage />} />
      
      {/* Payment pages - also available when authenticated */}
      <Route path="/payments" element={<PaymentsPage />} />
      <Route path="/payments/receipt/:invoiceId" element={<PaymentReceiptPage />} />
      <Route path="/payments/admin" element={<PaymentsAdminPage />} />
      
      {/* Customer account management */}
      <Route path="/account" element={<CustomerGate><AccountPage /></CustomerGate>} />
      <Route path="/account/:tab" element={<CustomerGate><AccountPage /></CustomerGate>} />
      
      {/* Retainer checkout — service tier engagement flow */}
      <Route path="/retainer-checkout" element={<RetainerCheckoutPage />} />

      {/* Commerce routes */}
      <Route path="/products" element={<PricingPage />} />
      <Route path="/cart" element={<CartPage />} />
      <Route path="/checkout" element={<CheckoutPage />} />
      <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
      <Route path="/checkout/cancel" element={<CheckoutCancelPage />} />
      <Route path="/checkout/bridge" element={<CheckoutBridgePage />} />
      
      {/* Admin routes with Layout */}
      <Route path="/*" element={
        <CustomerGate>
        <Layout>
          <Routes>
            {isCustomer ? (
            <>
            <Route path="/" element={<Navigate to="/topstep/bot" replace />} />
            <Route path="/dashboard" element={<Navigate to="/topstep/bot" replace />} />
            <Route path="/topstep/bot" element={<TopStepBotDashboardPage />} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/account/:tab" element={<AccountPage />} />
            <Route path="*" element={<Navigate to="/topstep/bot" replace />} />
            </>
            ) : (
            <>
            {/* Dashboard */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            
            {/* Discovery Dashboard */}
            <Route path="/discovery" element={<DiscoveryPage />} />
            
            {/* Authority Agent - Moltbook Protocol */}
            <Route path="/authority-agent" element={<AuthorityAgentPage />} />
            
            {/* Client Management */}
            <Route path="/clients" element={<ClientsPage />} />
            <Route path="/clients/:id" element={<ClientsPage />} />
            
            {/* Site Management */}
            <Route path="/sites" element={<SitesPage />} />
            <Route path="/sites/:id" element={<SitesPage />} />
            <Route path="/sites/:id/wizard" element={<SitesPage />} />
            
            {/* Invoice Management */}
            <Route path="/invoices" element={<InvoicesPage />} />
            <Route path="/invoices/:id" element={<InvoicesPage />} />
            
            {/* Email Management */}
            <Route path="/emails" element={<EmailsPage />} />
            <Route path="/emails/compose" element={<EmailsPage />} />
            <Route path="/emails/templates" element={<EmailsPage />} />
            
            {/* CRM Integration */}
            <Route path="/leads" element={<LeadsPage />} />
            <Route path="/contacts" element={<ContactsPage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            
            {/* Customer Management */}
            <Route path="/customers" element={<CustomersPage />} />
            <Route path="/customers/:id" element={<CustomerDetailPage />} />
            
            {/* Pipeline Management */}
            <Route path="/pipeline" element={<PipelineManagementPage />} />
            
            {/* REV108 Lead Generation */}
            <Route path="/rev108" element={<REV108Page />} />
            
            {/* Attention Engine */}
            <Route path="/attention-engine" element={<AttentionEnginePage />} />
            
            {/* Stratagem Nexus */}
            <Route path="/stratagem" element={<StratagemNexus />} />
            <Route path="/stratagem/opportunity/:id" element={<OpportunityDetailPage />} />
            
            {/* Opportunity Command Center - Revenue Focus */}
            <Route path="/opportunities" element={<OpportunityCommandCenter />} />
            
            {/* Communications Portal - Email/SMS */}
            <Route path="/communications" element={<CommunicationsPortal />} />
            
            {/* Iron Dome Security Center */}
            <Route path="/security" element={<IronDomePage />} />
            <Route path="/iron-dome" element={<IronDomePage />} />
            
            {/* Ops Dashboard — Private Capital Infrastructure */}
            <Route path="/ops" element={<OpsDashboardPage />} />
            
            {/* 富の神 Tomi-no-Kami Trading Dashboard */}
            <Route path="/tomi" element={<TomiDashboardPage />} />

            {/* 富の神 × 風の洞察 — Guided Trading Command Center */}
            <Route path="/tomi/command" element={<TomiCommandCenterPage />} />
            <Route path="/tomi/v2" element={<TomiV2CommandPage />} />

            {/* 風の洞察 Kaze no Dōsatsu Intelligence Dashboard */}
            <Route path="/kaze" element={<KazeDashboardPage />} />

            {/* Coinbase Derivatives — read-only Decision Lab */}
            <Route path="/coinbase" element={<CoinbaseDerivativesPage />} />

            {/* TopStep / ProjectX Challenge Command Center */}
            <Route path="/topstep" element={<TopStepCommandCenterPage />} />
            <Route path="/topstep/account2" element={<TopStepAccount2Page />} />
            <Route path="/topstep/account3" element={<TopStepAccount3Page />} />
            <Route path="/topstep/100k-challenge" element={<TopStep100KChallengePage />} />
            <Route path="/topstep/bot" element={<TopStepBotDashboardPage />} />
            <Route path="/topstep/150k-challenge" element={<TopStep150KChallengePage />} />
            <Route path="/topstep/josh-challenge" element={<TopStepJoshChallengePage />} />
            <Route path="/topstep/50k-challenge" element={<TopStep50KChallengePage />} />
            <Route path="/topstep/express-funded" element={<TopStepExpressFundedPage />} />
            <Route path="/topstep/express-funded-2" element={<TopStepExpressFunded2Page />} />
            <Route path="/topstep/nerdnode-funded" element={<TopStepNerdnodeFundedPage />} />
            </>
            )}
            
            {/* Catch all - redirect to dashboard */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Layout>
        </CustomerGate>
      } />
    </Routes>
  );
}

export default App;
